import { ReactNode, useMemo } from 'react';
import tw, { styled } from 'twin.macro';

import { Header, HEADER_HEIGHT, HeaderBranding } from '@components/Header';
import { FILTER_HEIGHT } from '@insights_module/components/shows_screen/FiltersMenu';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { InsightsSidebarNav } from '@insights_module/components/InsightsSidebarNav';
import { useLayoutContext } from '@insights_module/context/LayoutContext';
import { SCREENS, useMediaQueries } from '@app/hooks/useMediaQueries';

interface GridStyledComponentProps {
  $isSidebarVisible: boolean;
}

const Background = tw.div`min-h-screen flex flex-col justify-start items-center bg-no-repeat bg-cover bg-center bg-fixed bg-neutral-background text-text-and-icon`;
const Absolute = tw.div`absolute top-0 left-0 bottom-0 right-0 bg-page-background`;
const Card = tw.div`flex flex-col w-full h-full bg-neutral-background rounded-md border border-gray-3`;
const AsideHeaderBranding = tw.div`sm:hidden pl-[24px] border-b border-solid border-gray-3`;

const InsightsPageGrid = styled.div<GridStyledComponentProps>`
  ${tw`grid transition-all duration-200`}

  width: 100%;
  height: 100vh;
  max-height: 100vh;
  display; grid;
  grid-gap: 0;
  grid-template-columns: ${({ $isSidebarVisible }) => ($isSidebarVisible ? '320px auto auto' : '0 auto auto')};
  grid-template-areas: 
  "header header header" 
  "main main main";
  
  @media(min-width: 640px) {
    grid-template-columns: ${({ $isSidebarVisible }) => ($isSidebarVisible ? '254px auto auto' : '0 auto auto')};
    grid-template-areas: 
      "header header header"
      "aside main main";
  }
`;

const InsightsPageHeader = styled.header`
  grid-area: header;
  z-index: 95;
`;

const InsightsPageAside = styled.aside<GridStyledComponentProps>`
  ${tw`absolute sm:relative top-0 left-0 z-[100] w-[320px] sm:w-[254px] transition-all duration-200 bg-neutral-background overflow-hidden whitespace-nowrap border-r border-solid border-gray-3 h-full`};
  grid-area: aside;
  ${({ $isSidebarVisible }) => !$isSidebarVisible && tw`!w-0 border-0`}
`;

const InsightsPageMain = styled.main<GridStyledComponentProps>`
  ${tw`w-full transition-all duration-200 ease-in bg-page-background relative overflow-scroll`};
  grid-area: main;
  min-height: calc(100vh - ${HEADER_HEIGHT}px);

  ${({ $isSidebarVisible }) =>
    $isSidebarVisible &&
    tw`after:block after:bg-black after:opacity-30 after:fixed after:left-0 after:right-0 after:top-0 after:bottom-0 after:sm:relative after:sm:h-0 after:sm:w-0 z-[90] after:z-[90]`}
`;

const Layout = ({ children, ...props }: { children: React.ReactNode }) => {
  return <Layout.Background {...props}>{children}</Layout.Background>;
};

const ShowsTableWrapper = ({ children, ...props }: { children: React.ReactNode }) => {
  const { selectedShow, selectedEpisode } = useInsightsContext();

  const topOffset = useMemo(() => {
    if (selectedShow || selectedEpisode) return 0;

    return FILTER_HEIGHT;
  }, [selectedShow, selectedEpisode, FILTER_HEIGHT]);

  return (
    <Layout.Absolute tw='transition-all duration-200' css={{ top: topOffset }} {...props}>
      {children}
    </Layout.Absolute>
  );
};

const EpisodesDetailsWrapper = ({ children, ...props }: { children: React.ReactNode }) => {
  const { selectedEpisode } = useInsightsContext();

  if (!selectedEpisode) {
    return null;
  }

  return (
    <Layout.Absolute tw='transition-all duration-200 z-[51]' {...props}>
      {children}
    </Layout.Absolute>
  );
};

const InsightsPage = ({ children }: { children: ReactNode }) => {
  const { isSidebarVisible, setIsSidebarVisible } = useLayoutContext();
  const [isMobile] = useMediaQueries(SCREENS.SM);

  return (
    <InsightsPageGrid $isSidebarVisible={isSidebarVisible}>
      <InsightsPageHeader>
        <Header />
      </InsightsPageHeader>
      <InsightsPageAside $isSidebarVisible={isSidebarVisible}>
        <AsideHeaderBranding>
          <HeaderBranding />
        </AsideHeaderBranding>
        <InsightsSidebarNav />
      </InsightsPageAside>
      <InsightsPageMain
        onClick={() => isMobile && isSidebarVisible && setIsSidebarVisible(false)}
        $isSidebarVisible={isSidebarVisible}>
        {children}
      </InsightsPageMain>
    </InsightsPageGrid>
  );
};

Layout.displayName = 'Layout';
Layout.Background = Background;
Layout.Absolute = Absolute;
Layout.Card = Card;
Layout.ShowsTableWrapper = ShowsTableWrapper;
Layout.EpisodesDetailsWrapper = EpisodesDetailsWrapper;
Layout.InsightsPage = InsightsPage;

export { Layout };
